import React, { forwardRef } from 'react';
import useHeaderAutoFocus from '@tcp/core/src/components/features/CnC/SinglePageCheckout/container/hooks/useHeaderAutoFocus';
import PropTypes from 'prop-types';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import Button from '@tcp/core/src/components/common/atoms/Button';
import Image from '@tcp/core/src/components/common/atoms/Image';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import { getIconPath, isMobileWeb } from '../../../../../../utils';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '../../../../../../utils/labels/labelUtilty';
import {
  GLOBAL_ACTIONS_LABELS,
  GlobalActionsLabels,
  lblEdit,
} from '../../../../../../utils/labels/globalActions.labels';

const SectionHeaderTag = forwardRef((props, ref) => {
  const { isCollapseAble, children } = props;
  return isCollapseAble ? (
    <button ref={ref} {...props}>
      {children}
    </button>
  ) : (
    <div ref={ref} {...props}>
      {children}
    </div>
  );
});

function SectionHeader(props) {
  const {
    disabled,
    toggleExpansion,
    headerIcon,
    headerAltLabel,
    headerDataLocator,
    heading,
    headingDataLocator,
    showAction,
    actionLabel,
    actionClassName,
    actionAriaLabel,
    actionDataLocator,
    handleOnActionClick,
    actionLabelClassName,
    isCollapseAble,
    isExpanded,
    bossSwitchEnabled,
    fromMyBagCheckoutHeader,
  } = props;

  const [headingRef, actionRef] = useHeaderAutoFocus(isExpanded, showAction);

  return (
    <SectionHeaderTag
      fullBleed
      onClick={toggleExpansion}
      className={`spc-form-heading-container ${disabled ? 'disabled-section-header' : ''} ${
        isCollapseAble ? 'collapse-able-header' : 'sm-hide-outline'
      } ${bossSwitchEnabled ? 'boss-switch-links' : ''}`}
      aria-expanded={isExpanded ? 'true' : 'false'}
      isCollapseAble={isCollapseAble}
      ref={headingRef}
      tabindex={isExpanded ? 0 : -1}
    >
      {!bossSwitchEnabled && (
        <BodyCopy component="div" className="spc-form-heading">
          {headerIcon && (
            <Image
              className="header-icon elem-pr-XS"
              width="26px"
              height="26px"
              src={getIconPath(headerIcon)}
              alt={headerAltLabel}
              data-locator={headerDataLocator}
              aria-hidden="true"
            />
          )}
          <BodyCopy
            component="h2"
            className="spc-card-heading spc-title elem-pl-XS"
            data-locator={headingDataLocator}
          >
            {heading}
          </BodyCopy>
          {fromMyBagCheckoutHeader && (
            <div className="my-bag-checkout-edit-container">
              <BodyCopy
                className="my-bag-checkout-edit-divider-text"
                component="span"
                fontWeight="regular"
                fontSize={isMobileWeb() ? 'fs16' : 'fs20'}
                fontFamily="secondary"
              >
                |
              </BodyCopy>
              <BodyCopy component="span" fontSize="fs16" fontFamily="secondary">
                <Anchor
                  fontSizeVariation="medium"
                  underline
                  anchorVariation="primary"
                  to="/bag"
                  dataLocator="my-bag-checkout-edit-cta"
                  className="my-bag-checkout-edit-cta"
                  aria-label="my-bag-checkout-edit-cta"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="bag-container">
                    <BodyCopy
                      className="my-bag-checkout-edit-cta"
                      component="span"
                      fontWeight="semibold"
                      fontFamily="secondary"
                      fontSize={isMobileWeb() ? 'fs16' : 'fs20'}
                      aria-hidden="true"
                      tabIndex="-1"
                    >
                      {renderLabel(
                        getLabelFromCopyOrFromTranslation(
                          `${GLOBAL_ACTIONS_LABELS}.${lblEdit}`,
                          GlobalActionsLabels
                        ),
                        'lbl_edit'
                      )}
                    </BodyCopy>
                  </div>
                </Anchor>
              </BodyCopy>
            </div>
          )}
        </BodyCopy>
      )}
      {showAction && (
        <Button
          buttonVariation="category-links-dark"
          dataLocator={actionDataLocator}
          onClick={handleOnActionClick}
          noLink
          className={`sflActions spc-header-action expand-details-button sm-hide-outline ${actionClassName}`}
          aria-label={`${actionAriaLabel} ${heading}`}
          aria-hidden={isCollapseAble ? 'true' : 'false'}
          tabindex={isCollapseAble ? -1 : 0}
          ref={actionRef}
        >
          <BodyCopy
            fontFamily="secondary"
            fontSize="fs14"
            component="span"
            fontWeight={['semibold']}
            className={actionLabelClassName}
          >
            {actionLabel}
          </BodyCopy>
        </Button>
      )}
    </SectionHeaderTag>
  );
}

SectionHeaderTag.propTypes = {
  isCollapseAble: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

SectionHeaderTag.defaultProps = {
  isCollapseAble: false,
};
SectionHeader.propTypes = {
  disabled: PropTypes.bool,
  headerIcon: PropTypes.string,
  headerAltLabel: PropTypes.string,
  headerDataLocator: PropTypes.string,
  heading: PropTypes.string,
  headingDataLocator: PropTypes.string,
  actionLabel: PropTypes.string,
  actionAriaLabel: PropTypes.string,
  actionDataLocator: PropTypes.string,
  handleOnActionClick: PropTypes.func,
  showAction: PropTypes.bool,
  actionClassName: PropTypes.string,
  toggleExpansion: PropTypes.func,
  actionLabelClassName: PropTypes.string,
  isCollapseAble: PropTypes.bool,
  isExpanded: PropTypes.bool,
};

SectionHeader.defaultProps = {
  disabled: false,
  headerIcon: '',
  headerAltLabel: '',
  headerDataLocator: '',
  heading: '',
  headingDataLocator: '',
  actionLabel: '',
  actionAriaLabel: '',
  actionDataLocator: '',
  handleOnActionClick: undefined,
  showAction: false,
  actionClassName: '',
  toggleExpansion: undefined,
  actionLabelClassName: '',
  isCollapseAble: false,
  isExpanded: false,
};

export default SectionHeader;
